import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors as C } from "@/lib/theme";
import { Ion } from "@/components/Ion";
import { Screen, Spinner } from "@/components/Orig";
import { LessonSection, ModuleProgress } from "@/components/LsoLesson";

const Quiz = ({ qs, title }) => (
  <div className="mt-8 rounded-xl border p-4" style={{ borderColor: C.border, background: C.card }} data-testid="lso-preview-quiz">
    <p className="mb-3 text-xs font-extrabold tracking-[2px]" style={{ color: C.accent }}>{title} · ANSWERS SHOWN (ADMIN ONLY)</p>
    {qs.map((q, i) => (
      <div key={i} className="mb-4 border-t pt-3 first:border-t-0 first:pt-0" style={{ borderColor: C.border }}>
        <p className="mb-2 text-sm font-bold text-white">{i + 1}. {q.question}</p>
        {q.options.map((o, j) => (
          <p key={j} className={`mb-1 rounded-lg px-3 py-1.5 text-sm ${j === q.correct_index ? "bg-emerald-500/15 font-bold text-emerald-300" : "text-neutral-300"}`} data-testid={j === q.correct_index ? `lso-preview-correct-${i}` : undefined}>
            {"ABCDEFG"[j]}. {o}{j === q.correct_index ? "  ✓ correct" : ""}
          </p>
        ))}
        {q.explanation && <p className="mt-1 text-xs italic text-neutral-400">{q.explanation}</p>}
      </div>
    ))}
  </div>
);

const Tabs = ({ c, at, setAt }) => (
  <div className="mb-5 flex flex-wrap gap-1.5" data-testid="lso-preview-tabs">
    {[["intro", "Start"], ...c.modules.map((m) => [m.number, `M${m.number}`]), ["final", "Final exam"]].map(([k, l]) => (
      <button key={k} onClick={() => { setAt(k); window.scrollTo(0, 0); }} data-testid={`lso-preview-tab-${k}`}
        className="rounded-full border px-3 py-1 text-xs font-bold transition-colors" style={at === k ? { background: C.accent, color: "#000", borderColor: C.accent } : { borderColor: C.border, color: C.text }}>{l}</button>
    ))}
  </div>
);

const Intro = ({ c }) => (
  <div className="text-neutral-200" data-testid="lso-preview-intro">
    <h2 className="text-2xl font-extrabold text-white">{c.title}</h2>
    <p className="mt-1 text-sm text-neutral-400">{c.subtitle} · {c.standard} · pass mark {c.pass_threshold_percent}% · certificate valid {c.validity_days} days</p>
    {!!c.waiver_acknowledgments?.length && <>
      <p className="mb-2 mt-6 text-xs font-extrabold tracking-[2px]" style={{ color: C.accent }}>ACKNOWLEDGMENTS BUYERS SIGN BEFORE STARTING</p>
      <ul className="space-y-2">{c.waiver_acknowledgments.map((w, i) => <li key={i} className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: C.border }}>{w.text}</li>)}</ul>
    </>}
  </div>
);

export default function AdminLsoPreview() {
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [at, setAt] = useState("intro");
  useEffect(() => { api("/lso-rental/course/admin").then(setC).catch((e) => toast.error(e.message)); }, []);
  const mod = c?.modules.find((m) => m.number === at);
  return (
    <Screen testId="admin-lso-preview-page">
      <div className="mx-auto max-w-3xl px-4 pb-16">
        <div className="flex items-center gap-3 py-3">
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="lso-preview-back" className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border" style={{ background: C.card, borderColor: C.border }}><Ion name="chevron-back" size={22} color={C.text} /></button>
          <div className="flex-1"><p className="text-[9px] font-extrabold tracking-[1.6px]" style={{ color: C.accent }}>ADMIN · PREVIEW</p><h1 className="text-lg font-extrabold" data-testid="page-title">Customer LSO course</h1></div>
        </div>
        <p className="mb-4 text-xs" style={{ color: C.textSecondary }}>This is exactly what store buyers and rental customers read. Nothing here is graded or saved.</p>
        {!c ? <Spinner /> : <>
          <Tabs c={c} at={at} setAt={setAt} />
          {at === "intro" && <Intro c={c} />}
          {mod && <div data-testid="lso-preview-module">
            <ModuleProgress n={mod.number} total={c.modules.length} />
            <h2 className="mb-2 text-2xl font-extrabold text-white">Module {mod.number}: {mod.title}</h2>
            {mod.summary && <p className="mb-8 border-l-2 pl-4 text-base italic text-neutral-300" style={{ borderColor: C.accent }}>{mod.summary}</p>}
            {mod.sections.map((s, i) => <LessonSection key={i} s={s} i={i} />)}
            {!!mod.quiz?.length && <Quiz qs={mod.quiz} title={`MODULE ${mod.number} QUIZ`} />}
          </div>}
          {at === "final" && <Quiz qs={c.final_exam || []} title={`FINAL EXAM · ${(c.final_exam || []).length} QUESTIONS`} />}
          <div className="mt-8 flex justify-between">
            {at !== "intro" && <button onClick={() => { setAt(at === "final" ? c.modules.length : at === 1 ? "intro" : at - 1); window.scrollTo(0, 0); }} className="rounded-full border px-4 py-2 text-sm font-bold" style={{ borderColor: C.border, color: C.text }} data-testid="lso-preview-prev">← Previous</button>}
            {at !== "final" && <button onClick={() => { setAt(at === "intro" ? 1 : at === c.modules.length ? "final" : at + 1); window.scrollTo(0, 0); }} className="ml-auto rounded-full px-4 py-2 text-sm font-bold text-black" style={{ background: C.accent }} data-testid="lso-preview-next">Next →</button>}
          </div>
        </>}
      </div>
    </Screen>
  );
}
